import "@/instrument";

// biome-ignore lint/performance/noNamespaceImport: Part of Sentry initialization
import * as Sentry from "@sentry/react";
import React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { createBrowserRouter } from "react-router";
import { RouterProvider } from "react-router/dom";
import "@/i18n";

import { AuthRoute } from "@/components/auth-route";
import { BuildVersionGuard } from "@/components/build-version-guard";
import {
  ErrorBoundary,
  RouterErrorFallback,
} from "@/components/error-boundary";
import { Layout } from "@/components/layout";
import { Maintenance } from "@/components/maintenance";
import {
  EasiRechargeProtectedRoute,
  ProtectedRoute,
} from "@/components/protected-route";
import { SessionProvider } from "@/components/session-provider";
import { Toaster } from "@/components/ui/sonner";
import Checkout from "@/pages/checkout";
import CustomerInformation from "@/pages/customer-information";
import EasiRechargeReceipt from "@/pages/easi-recharge/receipt";
import EasiRechargeSelectPackage from "@/pages/easi-recharge/select-package";
import EasiRechargeSummary from "@/pages/easi-recharge/summary";
import Home from "@/pages/home";
import NotFound from "@/pages/not-found";
import PayWithWallet from "@/pages/pay-with-wallet";
import Payment from "@/pages/payment";
import Plans from "@/pages/plans";
import Receipt from "@/pages/receipt";
import SelectNumber from "@/pages/select-number";
import Sitemap from "@/pages/sitemap";
import Summary from "@/pages/summary";
import TermsAndConditions from "@/pages/terms-and-conditions";

import store from "@/redux/store";
import "@/styles/globals.css";

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

const router = sentryCreateBrowserRouter([
  {
    path: "/",
    element: (
      <>
        <BuildVersionGuard />
        <ErrorBoundary>
          <Layout />
        </ErrorBoundary>
      </>
    ),
    errorElement: <RouterErrorFallback />,
    children: [
      { index: true, element: <Home /> },
      {
        path: "/:service/plans",
        element: (
          <ProtectedRoute>
            <Plans />
          </ProtectedRoute>
        ),
      },
      {
        path: "/:service/select-number",
        element: (
          <ProtectedRoute>
            <SelectNumber />
          </ProtectedRoute>
        ),
      },
      {
        path: "/:service/customer-information",
        element: (
          <ProtectedRoute>
            <CustomerInformation />
          </ProtectedRoute>
        ),
      },
      {
        path: "/:service/summary",
        element: (
          <ProtectedRoute>
            <Summary />
          </ProtectedRoute>
        ),
      },
      {
        path: "/:service/checkout",
        element: (
          <ProtectedRoute>
            <Checkout />
          </ProtectedRoute>
        ),
      },
      {
        path: "/pay-with-wallet/:id",
        element: (
          <AuthRoute>
            <ProtectedRoute>
              <PayWithWallet />
            </ProtectedRoute>
          </AuthRoute>
        ),
      },
      { path: "/payment/:id", element: <Payment /> },
      { path: "/retrive_payment/:id", element: <Payment /> },
      { path: "/receipt/:id", element: <Receipt /> },
      {
        path: "/easi-recharge/select-package",
        element: (
          <AuthRoute>
            <EasiRechargeSelectPackage />
          </AuthRoute>
        ),
      },
      {
        path: "/easi-recharge/summary",
        element: (
          <AuthRoute>
            <EasiRechargeProtectedRoute>
              <EasiRechargeSummary />
            </EasiRechargeProtectedRoute>
          </AuthRoute>
        ),
      },
      {
        path: "/easi-recharge/receipt",
        element: (
          <AuthRoute>
            <EasiRechargeProtectedRoute>
              <EasiRechargeReceipt />
            </EasiRechargeProtectedRoute>
          </AuthRoute>
        ),
      },
      { path: "/terms-and-conditions", element: <TermsAndConditions /> },
      { path: "/sitemap", element: <Sitemap /> },
      { path: "*", element: <NotFound /> },
    ],
  },
]);

const maintenanceRouter = createBrowserRouter([
  {
    path: "/",
    element: (
      <>
        <BuildVersionGuard />
        <Maintenance />
      </>
    ),
  },
  {
    path: "*",
    element: (
      <>
        <BuildVersionGuard />
        <Maintenance />
      </>
    ),
  },
]);

const container = document.getElementById("root");
// @ts-expect-error Targets the root element
const root = createRoot(container, {
  // Callback called when an error is thrown and not caught by an ErrorBoundary.
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn("Uncaught error", error, errorInfo.componentStack);
  }),
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: Sentry.reactErrorHandler(),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: Sentry.reactErrorHandler(),
});

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <SessionProvider>
        <RouterProvider
          router={
            import.meta.env.VITE_ENV === "maintenance"
              ? maintenanceRouter
              : router
          }
        />
        <Toaster richColors />
      </SessionProvider>
    </Provider>
  </React.StrictMode>
);
